// src/resources/dashboards/ProductEvolution/sections/PenetrationSection.tsx
//
// Penetração do app na pista: quanto de TUDO que passou pela bomba o PontuaX
// consegue identificar. O denominador vem de pump_delivery_daily — medir isso
// pela distribuição de `origin` no Postgres daria ~81%, que é falso, porque ao
// Postgres só chega o que o PontuaX já recebeu.
//
// `available: false` é o TERCEIRO estado da tela: não é erro (o backend
// respondeu 200) e não é vazio (não existe fonte pra esta rede). Fica dentro do
// card, com o SectionHeader preservado e o `reason` que o backend mandou.
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import { motion } from 'framer-motion';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis,
} from 'recharts';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum } from '../../shared';
import type { SectionState } from '../../shared';
import { ChainPenetrationData } from '../types';
import {
  BackendNote, Card, MetricBar, ShowingCount, Stat, StatGrid, UnavailableHint,
  bucketLabel, fmtDate, fmtPctSmall, fmtPp,
} from './kit';

const HEIGHT = 460;

/** Quantos postos entram no ranking do card. Acima disto a lista vira rolagem. */
const PLACES_MAX_HEIGHT = 260;

const Gauge = ({ pct, deltaPp }: { pct: number; deltaPp: number | null }) => {
  const circumference = 2 * Math.PI * 60;
  return (
    <Box sx={{ position: 'relative', width: 150, height: 150, flexShrink: 0 }}>
      <svg width={150} height={150} viewBox="0 0 150 150">
        <circle cx={75} cy={75} r={60} fill="none" stroke="#f1f5f9" strokeWidth={16} />
        <motion.circle
          cx={75} cy={75} r={60} fill="none" stroke={COLORS.blue} strokeWidth={16} strokeLinecap="round"
          strokeDasharray={circumference} transform="rotate(-90 75 75)"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference * (1 - Math.max(0, Math.min(1, pct))) }}
          transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1] }}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {/* fmtPctSmall: 0,4% de penetração não pode aparecer como "0%". */}
        <Typography sx={{ fontSize: 27, fontWeight: 800, color: COLORS.ink }}>{fmtPctSmall(pct)}</Typography>
        {deltaPp !== null && (
          <Typography sx={{ fontSize: 10, color: deltaPp >= 0 ? COLORS.success : COLORS.danger }}>{fmtPp(deltaPp)}</Typography>
        )}
      </Box>
    </Box>
  );
};

const PenetrationSection = ({ state }: { state: SectionState<ChainPenetrationData> }) => {
  const theme = useTheme();
  if (state.loading && !state.data) return <SkeletonCard height={HEIGHT} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={HEIGHT} />;
  if (!state.data) return null;

  const data = state.data;
  const chartFont = theme.typography.fontFamily;

  const header = (
    <SectionHeader
      icon={<SensorsIcon sx={{ fontSize: 18 }} />}
      gradient={`linear-gradient(135deg, ${COLORS.blue}, #06b6d4)`}
      title="Penetração do app na pista"
      subtitle="De tudo que passou pela bomba, quanto o PontuaX identificou"
    />
  );

  // Indisponível: header preservado, motivo do backend dentro do card.
  if (!data.available || !data.overall) {
    return (
      <Box>
        {header}
        <Box sx={cardSx}>
          <UnavailableHint reason={data.reason} />
        </Box>
      </Box>
    );
  }

  const { overall, series, byPlace } = data;
  const chartData = series
    .filter(b => b.appPenetration !== null)
    .map(b => ({
      label: bucketLabel(b.bucket, data.bucketUnit),
      penetracao: Number(((b.appPenetration ?? 0) * 100).toFixed(2)),
      entregas: b.deliveries,
      appEntregas: b.appDeliveries,
    }));

  const rankedPlaces = byPlace.filter(p => p.deliveries > 0);
  const valueShare = overall.totalValue > 0 ? overall.appValue / overall.totalValue : null;

  return (
    <Box>
      {header}
      <Box sx={{ display: 'grid', gap: 1.75 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.75 }}>
          <Card title="Captura da rede" hint="Entregas de bomba identificadas pelo app no período">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
              {overall.appPenetration !== null ? (
                <Gauge pct={overall.appPenetration} deltaPp={overall.deltaPp} />
              ) : (
                <Box sx={{ width: 150, height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: COLORS.faint, textAlign: 'center' }}>
                    Sem entregas de bomba no período
                  </Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <StatGrid min={132}>
                  <Stat label="Entregas totais" value={fmtNum(overall.deliveries)} />
                  <Stat label="Pelo app" value={fmtNum(overall.appDeliveries)} color={COLORS.blue} />
                  <Stat label="Sem app" value={fmtNum(overall.pumpDeliveries)} color={COLORS.faint} />
                  <Stat label="Valor identificado" value={fmtBrl(overall.appValue)} color={COLORS.brand}
                    sub={valueShare !== null ? `${fmtPctSmall(valueShare)} de ${fmtBrl(overall.totalValue)}` : undefined} />
                </StatGrid>
                {overall.previousAppPenetration !== null && (
                  <Typography sx={{ fontSize: 11.5, color: COLORS.muted, mt: 1.25 }}>
                    Período anterior: <b>{fmtPctSmall(overall.previousAppPenetration)}</b>
                  </Typography>
                )}
              </Box>
            </Box>
            <BackendNote>
              Dados de pista de {fmtDate(data.dataFrom)} a {fmtDate(data.dataThrough)} · a ingestão é D-1.
            </BackendNote>
          </Card>

          <Card title="Tendência da penetração" hint={`Por ${data.bucketUnit === 'week' ? 'semana' : 'dia'}, em % das entregas de bomba`}>
            {!chartData.length ? (
              <EmptyHint text="Sem entregas de bomba registradas neste período." />
            ) : (
              <Box sx={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="penetrationFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.blue} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={COLORS.blue} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.muted }}
                      tickLine={false} axisLine={{ stroke: 'rgba(148,163,184,0.35)' }} minTickGap={16} />
                    <YAxis tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.muted }}
                      tickLine={false} axisLine={false} width={44}
                      tickFormatter={(v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`} />
                    <ReTooltip
                      cursor={{ stroke: 'rgba(59,130,246,0.35)' }}
                      contentStyle={{ fontFamily: chartFont, fontSize: 12, borderRadius: 8, border: '1px solid rgba(120,105,223,0.2)' }}
                      formatter={(value: any, name: any) => (
                        name === 'Penetração'
                          ? [`${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`, name]
                          : [fmtNum(Number(value)), name]
                      )}
                    />
                    <Area dataKey="penetracao" name="Penetração" stroke={COLORS.blue} strokeWidth={2} fill="url(#penetrationFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Card>
        </Box>

        <Card
          title="Penetração por posto"
          hint="Ordenado da maior penetração para a menor"
          right={<ShowingCount shown={rankedPlaces.length} total={rankedPlaces.length} noun="posto" />}
        >
          {!rankedPlaces.length ? (
            <EmptyHint text="Nenhum posto com entrega de bomba no período." />
          ) : (
            <>
              <Box sx={{ maxHeight: PLACES_MAX_HEIGHT, overflowY: 'auto', pr: 0.5 }}>
                {rankedPlaces.map(p => (
                  <MetricBar
                    key={p.placeId}
                    label={p.name}
                    value={`${fmtPctSmall(p.appPenetration)} · ${fmtNum(p.appDeliveries)} de ${fmtNum(p.deliveries)}`}
                    // A escala vai até 50%: penetração de pista raramente passa
                    // disso e uma escala 0-100% deixaria todas as barras rasas.
                    pctOfMax={(p.appPenetration ?? 0) / 0.5}
                    color={(p.appPenetration ?? 0) >= 0.15
                      ? `linear-gradient(90deg, ${COLORS.blue}, #06b6d4)`
                      : `linear-gradient(90deg, ${COLORS.warning}, #fbbf24)`}
                    labelWidth={168}
                  />
                ))}
              </Box>
              <Typography sx={{ fontSize: 11, color: COLORS.faint, mt: 0.75 }}>
                Escala das barras vai até 50% · azul a partir de 15% de penetração, âmbar abaixo disso.
              </Typography>
              <BackendNote>
                Clientes digitais: {data.digitalCustomersNote
                  ?? 'disponível apenas por posto — somar entre postos contaria duas vezes quem abastece em mais de um.'}
                {' '}Por posto, o pico diário vai de{' '}
                {fmtNum(Math.min(...rankedPlaces.map(p => p.digitalCustomersDailyPeak)))} a{' '}
                {fmtNum(Math.max(...rankedPlaces.map(p => p.digitalCustomersDailyPeak)))} clientes.
              </BackendNote>
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default PenetrationSection;
